import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ICON_BTN = 'inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

/**
 * Row actions: icon buttons with a tooltip on wider screens, a labelled "⋯" menu on phones
 * (tooltips don't open on touch).
 *
 * actions: [{ key, label, hint?, icon: Icon, onClick? | href?, download?, newTab?, className?, testId? }]
 * `label` names the action; `hint` (optional) explains what it does and is shown in the tooltip.
 */
export default function RowActions({ actions }) {
  const { t } = useTranslation('rowActions');
  const list = actions.filter(Boolean);
  if (!list.length) return null;
  return (
    <>
      <TooltipProvider delayDuration={150}>
        <div className="hidden sm:flex items-center gap-0.5 justify-end">
          {list.map((a) => {
            const Icon = a.icon;
            const common = { className: `${ICON_BTN} ${a.className || ''}`, 'aria-label': a.label, 'data-testid': a.testId };
            return (
              <Tooltip key={a.key}>
                <TooltipTrigger asChild>
                  {a.href ? (
                    <a href={a.href} {...(a.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      {...(a.download ? { download: '' } : {})} {...common}><Icon className="h-4 w-4" /></a>
                  ) : (
                    <button type="button" onClick={a.onClick} {...common}><Icon className="h-4 w-4" /></button>
                  )}
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px]">
                  <p className="font-medium">{a.label}</p>
                  {a.hint && <p className="opacity-80 mt-0.5">{a.hint}</p>}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
      <div className="sm:hidden flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={ICON_BTN} aria-label={t('menu')}><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {list.map((a) => {
              const Icon = a.icon;
              const body = (
                <span className="flex items-start gap-2">
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${a.className || ''}`} />
                  <span><span className="block">{a.label}</span>{a.hint && <span className="block text-xs text-slate-500">{a.hint}</span>}</span>
                </span>
              );
              return a.href ? (
                <DropdownMenuItem key={a.key} asChild>
                  <a href={a.href} {...(a.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...(a.download ? { download: '' } : {})}>{body}</a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem key={a.key} onSelect={() => a.onClick?.()}>{body}</DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
